import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

type Pt = [number, number];

const MAIN_ROUGH: Pt[] = [
  [37.9, 9.2],
  [77.3, 25.8],
  [93.1, 47.2],
  [97.8, 80.3],
  [85.2, 107.5],
  [50.5, 105.9],
  [26.9, 86.8],
  [20.6, 53.4],
  [23.8, 26.1],
  [37.9, 9.2],
];
const MAIN_REFINED: Pt[] = [
  [60, 15],
  [79.7, 32.3],
  [91.5, 58.9],
  [87.6, 84],
  [74.2, 98.1],
  [53.7, 98.6],
  [34.8, 82.5],
  [26.9, 57.8],
  [30.1, 33.4],
  [45.8, 18.9],
];
const LIGHT_ROUGH: Pt[] = [
  [37.9, 9.2],
  [77.3, 25.8],
  [82.1, 50.8],
  [56.8, 51.3],
  [30.1, 35.4],
  [37.9, 9.2],
  [37.9, 9.2],
  [37.9, 9.2],
  [37.9, 9.2],
  [37.9, 9.2],
];
const LIGHT_REFINED: Pt[] = [
  [60, 15],
  [79.7, 32.3],
  [74.2, 53.1],
  [56.1, 44.1],
  [45.8, 18.9],
  [60, 15],
  [60, 15],
  [60, 15],
  [60, 15],
  [60, 15],
];
const DRIGHT_ROUGH: Pt[] = [
  [77.3, 25.8],
  [93.1, 47.2],
  [97.8, 80.3],
  [85.2, 107.5],
  [82.1, 50.8],
  [77.3, 25.8],
  [77.3, 25.8],
  [77.3, 25.8],
  [77.3, 25.8],
  [77.3, 25.8],
];
const DRIGHT_REFINED: Pt[] = [
  [79.7, 32.3],
  [91.5, 58.9],
  [87.6, 84],
  [74.2, 98.1],
  [74.2, 53.1],
  [79.7, 32.3],
  [79.7, 32.3],
  [79.7, 32.3],
  [79.7, 32.3],
  [79.7, 32.3],
];
const DLEFT_ROUGH: Pt[] = [
  [37.9, 9.2],
  [30.1, 35.4],
  [26.9, 86.8],
  [20.6, 53.4],
  [23.8, 26.1],
  [37.9, 9.2],
  [37.9, 9.2],
  [37.9, 9.2],
  [37.9, 9.2],
  [37.9, 9.2],
];
const DLEFT_REFINED: Pt[] = [
  [45.8, 18.9],
  [56.1, 44.1],
  [53.7, 98.6],
  [34.8, 82.5],
  [26.9, 57.8],
  [30.1, 33.4],
  [45.8, 18.9],
  [45.8, 18.9],
  [45.8, 18.9],
  [45.8, 18.9],
];

const LABEL_KEYS = [
  'accountMapping.loader.labels.stage1',
  'accountMapping.loader.labels.stage2',
  'accountMapping.loader.labels.stage3',
  'accountMapping.loader.labels.stage4',
  'accountMapping.loader.labels.stage5',
] as const;

const HIT_MS = 500;
const HITS_PER_CYCLE = 20;

interface Chip {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  life: number;
  maxLife: number;
  goldness: number;
  rot: number;
  rotV: number;
  spark: boolean;
}

function lerpPt(a: Pt, b: Pt, t: number): Pt {
  return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];
}

function buildPath(rough: Pt[], refined: Pt[], t: number): string {
  const pts = rough.map((p, i) => lerpPt(p, refined[i], t));
  return 'M' + pts.map((p) => `${p[0].toFixed(2)} ${p[1].toFixed(2)}`).join(' L') + ' Z';
}

function lerpC(a: number, b: number, t: number): number {
  return Math.round(a + (b - a) * t);
}

function getColors(t: number) {
  return {
    // eslint-disable-next-line no-restricted-syntax -- ui-policy-exempt UI-10: canvas logo animation interpolates brand artwork colors at runtime.
    main: `rgb(${lerpC(0x78, 0xe6, t)},${lerpC(0x78, 0xb8, t)},${lerpC(0x78, 0x00, t)})`,
    // eslint-disable-next-line no-restricted-syntax -- ui-policy-exempt UI-10: canvas logo animation interpolates brand artwork colors at runtime.
    light: `rgb(${lerpC(0x9a, 0xf0, t)},${lerpC(0x9a, 0xd0, t)},${lerpC(0x9a, 0x40, t)})`,
    // eslint-disable-next-line no-restricted-syntax -- ui-policy-exempt UI-10: canvas logo animation interpolates brand artwork colors at runtime.
    darkRight: `rgb(${lerpC(0x5a, 0xc8, t)},${lerpC(0x5a, 0xa0, t)},${lerpC(0x5a, 0x00, t)})`,
    // eslint-disable-next-line no-restricted-syntax -- ui-policy-exempt UI-10: canvas logo animation interpolates brand artwork colors at runtime.
    darkLeft: `rgb(${lerpC(0x62, 0xd0, t)},${lerpC(0x62, 0xa8, t)},${lerpC(0x62, 0x00, t)})`,
  };
}

export interface AlchemyDataLoaderProps {
  /** Tab IDs currently running AI suggestion */
  pendingTabIds?: string[];
  /** Tabs that just finished (success or error) — shown until dismissed */
  completedTabs?: { tabId: string; status: 'success' | 'error' }[];
  /** Called when user dismisses the completed state */
  onDismiss?: () => void;
  /** Estimated duration in seconds for the progress bar (when there are pending tabs) */
  durationSeconds?: number;
  /** Sticky bottom banner (default) or centered inline panel for embedded surfaces */
  variant?: 'banner' | 'inline';
  /** Keep the mineral animation visible without pending AI tabs (e.g. route lazy-load) */
  forceActive?: boolean;
  /** Hide the pending tab label row under the stage title */
  hideTabLabels?: boolean;
  /** Render only the animation with an optional label. */
  animationOnly?: boolean;
  /** Label shown below the animation-only variant. */
  animationLabel?: string;
}

export function AlchemyDataLoader({
  pendingTabIds = [],
  completedTabs = [],
  onDismiss,
  durationSeconds = 60,
  variant = 'banner',
  forceActive = false,
  hideTabLabels = false,
  animationOnly = false,
  animationLabel,
}: AlchemyDataLoaderProps) {
  const { t } = useTranslation('tenantSettings');

  const svgRef = useRef<SVGSVGElement>(null);
  const mainRef = useRef<SVGPathElement>(null);
  const lightRef = useRef<SVGPathElement>(null);
  const drRef = useRef<SVGPathElement>(null);
  const dlRef = useRef<SVGPathElement>(null);

  const nuggetZoneRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const glowRef = useRef<HTMLDivElement>(null);
  const fillRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLParagraphElement>(null);

  const state = useRef({
    running: false,
    rafId: 0,
    startTime: 0,
    lastFrameTime: 0,
    cycleHits: 0,
    lastHit: -Infinity,
    cycleDisplayT: 0,
    cycleTargetT: 0,
    shakeX: 0,
    shakeY: 0,
    shakeDecay: 0,
    chips: [] as Chip[],
    currentLabelIdx: -1,
    totalDuration: durationSeconds * 1000,
  });

  const hasPending = forceActive || pendingTabIds.length > 0;
  const hasCompleted = completedTabs.length > 0;
  const visible = forceActive || hasPending || hasCompleted;

  const applyShape = useCallback((tVal: number) => {
    const c = getColors(tVal);
    mainRef.current?.setAttribute('d', buildPath(MAIN_ROUGH, MAIN_REFINED, tVal));
    mainRef.current?.setAttribute('fill', c.main);
    lightRef.current?.setAttribute('d', buildPath(LIGHT_ROUGH, LIGHT_REFINED, tVal));
    lightRef.current?.setAttribute('fill', c.light);
    drRef.current?.setAttribute('d', buildPath(DRIGHT_ROUGH, DRIGHT_REFINED, tVal));
    drRef.current?.setAttribute('fill', c.darkRight);
    dlRef.current?.setAttribute('d', buildPath(DLEFT_ROUGH, DLEFT_REFINED, tVal));
    dlRef.current?.setAttribute('fill', c.darkLeft);
  }, []);

  const spawnChips = useCallback((tVal: number) => {
    const s = state.current;
    const cx = 65;
    const cy = 60;
    const count = 4 + Math.floor((1 - tVal) * 5);
    for (let i = 0; i < count; i++) {
      const angle = ((Math.random() * 200 + 150) * Math.PI) / 180;
      const speed = 1.6 + Math.random() * 3.0;
      const size = 2.5 + Math.random() * 4;
      const chip: Chip = {
        x: cx + (Math.random() - 0.5) * 32,
        y: cy + (Math.random() - 0.5) * 28,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed - 0.4,
        size,
        life: 500 + Math.random() * 500,
        maxLife: 0,
        goldness: Math.min(1, tVal + Math.random() * 0.35),
        rot: Math.random() * Math.PI * 2,
        rotV: (Math.random() - 0.5) * 0.16,
        spark: false,
      };
      chip.maxLife = chip.life;
      s.chips.push(chip);
    }
    for (let i = 0; i < 5; i++) {
      s.chips.push({
        x: cx + (Math.random() - 0.5) * 22,
        y: cy - 12 + (Math.random() - 0.5) * 18,
        vx: (Math.random() - 0.5) * 3,
        vy: -1.4 - Math.random() * 2.4,
        size: 1.2 + Math.random() * 1.8,
        life: 160 + Math.random() * 140,
        maxLife: 300,
        goldness: 1,
        spark: true,
        rot: 0,
        rotV: 0,
      });
    }
  }, []);

  const tickChips = useCallback((dt: number) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const w = canvas.width;
    const h = canvas.height;
    ctx.clearRect(0, 0, w, h);
    const chips = state.current.chips;
    const gravity = 0.055;

    for (let i = chips.length - 1; i >= 0; i--) {
      const c = chips[i];
      c.x += c.vx;
      c.y += c.vy;
      c.vy += gravity;
      c.rot += c.rotV;
      c.life -= dt;
      if (c.life <= 0) {
        chips.splice(i, 1);
        continue;
      }

      const prog = 1 - c.life / c.maxLife;
      const alpha = (1 - prog) * (c.spark ? 0.95 : 0.88);

      ctx.save();
      ctx.globalAlpha = alpha;
      ctx.translate(c.x, c.y);
      ctx.rotate(c.rot);

      if (c.spark) {
        ctx.beginPath();
        ctx.arc(0, 0, c.size, 0, Math.PI * 2);
        // eslint-disable-next-line no-restricted-syntax -- ui-policy-exempt UI-10: canvas particles calculate a continuous brand-gold luminance ramp.
        ctx.fillStyle = `hsl(48,100%,${68 + prog * 20}%)`;
        ctx.fill();
      } else {
        const sz = c.size * (1 - prog * 0.25);
        ctx.beginPath();
        ctx.moveTo(0, -sz);
        ctx.lineTo(sz * 0.6, -sz * 0.1);
        ctx.lineTo(sz * 0.4, sz * 0.75);
        ctx.lineTo(-sz * 0.3, sz * 0.55);
        ctx.lineTo(-sz * 0.65, 0);
        ctx.closePath();
        const sat = Math.round(c.goldness * 86);
        const lum = Math.round(36 + c.goldness * 28 + prog * 12);
        ctx.fillStyle =
          c.goldness > 0.45
            ? `hsl(43,${sat}%,${lum}%)` // eslint-disable-line no-restricted-syntax -- ui-policy-exempt UI-10: canvas particles calculate continuous gold and neutral luminance ramps.
            : `hsl(0,0%,${40 + prog * 22}%)`; // eslint-disable-line no-restricted-syntax -- ui-policy-exempt UI-10: canvas particles calculate continuous gold and neutral luminance ramps.
        ctx.fill();
        ctx.beginPath();
        ctx.moveTo(0, -sz);
        ctx.lineTo(sz * 0.6, -sz * 0.1);
        // eslint-disable-next-line no-restricted-syntax -- ui-policy-exempt UI-10: canvas particle links calculate opacity for inverse artwork strokes.
        ctx.strokeStyle = `rgba(255,255,255,${(alpha * 0.35).toFixed(2)})`;
        ctx.lineWidth = 0.7;
        ctx.stroke();
      }
      ctx.restore();
    }
  }, []);

  const updateLabel = useCallback(
    (globalT: number) => {
      const s = state.current;
      const idx = Math.min(Math.floor(globalT * LABEL_KEYS.length), LABEL_KEYS.length - 1);
      if (idx === s.currentLabelIdx) return;
      s.currentLabelIdx = idx;
      const el = titleRef.current;
      if (!el) return;
      el.style.opacity = '0';
      setTimeout(() => {
        if (titleRef.current) {
          titleRef.current.textContent = t(LABEL_KEYS[idx]);
          titleRef.current.style.opacity = '1';
        }
      }, 350);
    },
    [t],
  );

  const resetCycle = useCallback(() => {
    const s = state.current;
    s.cycleHits = 0;
    s.cycleDisplayT = 0;
    s.cycleTargetT = 0;
    s.lastHit = -Infinity;
    s.currentLabelIdx = -1;
    s.chips.length = 0;
    if (glowRef.current) glowRef.current.style.opacity = '0';
    applyShape(0);
  }, [applyShape]);

  const tick = useCallback(
    (now: number) => {
      const s = state.current;
      const dt = s.lastFrameTime ? Math.min(now - s.lastFrameTime, 60) : 16;
      s.lastFrameTime = now;
      if (!s.startTime) s.startTime = now;
      const elapsed = now - s.startTime;

      const globalT = Math.min(elapsed / s.totalDuration, 1);
      if (fillRef.current) fillRef.current.style.width = `${globalT * 100}%`;

      if (elapsed >= s.totalDuration) {
        s.running = false;
        return;
      }

      if (now - s.lastHit >= HIT_MS) {
        s.lastHit = now;
        s.cycleHits++;
        if (s.cycleHits > HITS_PER_CYCLE) {
          resetCycle();
          s.lastHit = now;
        } else {
          s.cycleTargetT = s.cycleHits / HITS_PER_CYCLE;
          s.shakeX = (Math.random() - 0.5) * 10;
          s.shakeY = (Math.random() - 0.5) * 7 - 2;
          s.shakeDecay = 1.0;
          spawnChips(s.cycleTargetT);
          updateLabel(globalT);
        }
      }

      s.cycleDisplayT += (s.cycleTargetT - s.cycleDisplayT) * Math.min(1, 0.07 * (dt / 16));
      applyShape(s.cycleDisplayT);

      if (s.shakeDecay > 0.008) {
        s.shakeDecay *= Math.pow(0.68, dt / 16);
        if (stageRef.current) {
          stageRef.current.style.transform = `translate(${(s.shakeX * s.shakeDecay).toFixed(
            2,
          )}px,${(s.shakeY * s.shakeDecay).toFixed(2)}px)`;
        }
      } else {
        s.shakeDecay = 0;
        if (stageRef.current) stageRef.current.style.transform = '';
      }

      if (glowRef.current) glowRef.current.style.opacity = (s.cycleDisplayT * 0.92).toFixed(3);

      if (svgRef.current) {
        const tVal = s.cycleDisplayT;
        const blur = (4 + tVal * 24).toFixed(1);
        const r = lerpC(80, 230, tVal);
        const g = lerpC(80, 184, tVal);
        const b = lerpC(80, 0, tVal);
        const op = (0.1 + tVal * 0.52).toFixed(2);
        // eslint-disable-next-line no-restricted-syntax -- ui-policy-exempt UI-10: animated logo shadow samples the active artwork gradient at runtime.
        svgRef.current.style.filter = `drop-shadow(0 6px ${blur}px rgba(${r},${g},${b},${op}))`;
      }

      tickChips(dt);

      if (s.running) s.rafId = requestAnimationFrame(tick);
    },
    [applyShape, resetCycle, spawnChips, tickChips, updateLabel],
  );

  const resizeCanvas = useCallback(() => {
    const zone = nuggetZoneRef.current;
    const canvas = canvasRef.current;
    if (!zone || !canvas) return;
    const r = zone.getBoundingClientRect();
    canvas.width = r.width;
    canvas.height = r.height;
  }, []);

  useEffect(() => {
    if (!hasPending) return;

    const canvas = canvasRef.current;
    const s = state.current;
    s.totalDuration = durationSeconds * 1000;
    s.running = true;
    s.startTime = 0;
    s.lastFrameTime = 0;
    resetCycle();
    updateLabel(0);
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);
    s.rafId = requestAnimationFrame(tick);

    return () => {
      s.running = false;
      cancelAnimationFrame(s.rafId);
      window.removeEventListener('resize', resizeCanvas);
      if (canvas) canvas.getContext('2d')?.clearRect(0, 0, canvas.width, canvas.height);
    };
  }, [hasPending, durationSeconds, resetCycle, tick, updateLabel, resizeCanvas]);

  // Auto-close once all processing is finished, after briefly showing the completed state.
  useEffect(() => {
    if (hasPending || !hasCompleted) return;
    if (!onDismiss) return;

    const timeoutId = window.setTimeout(() => {
      onDismiss();
    }, 1800);

    return () => window.clearTimeout(timeoutId);
  }, [hasPending, hasCompleted, onDismiss]);

  if (!visible) return null;

  const pendingLabels = pendingTabIds.map((id) => t(`accountMapping.tabs.${id}`)).join(', ');
  const completedLabels = completedTabs.map((c) => t(`accountMapping.tabs.${c.tabId}`));

  return (
    <>
      <style>{`@keyframes alchemy-dot-pulse{0%,100%{opacity:1;transform:scale(1)}50%{opacity:.5;transform:scale(.85)}}`}</style>
      <div
        className={cn(
          variant === 'inline'
            ? 'flex h-full min-h-[240px] w-full items-center justify-center bg-background p-8'
            : cn(
                'sticky bottom-0 inset-x-0 z-[30]',
                'h-[20vh] min-h-[130px] max-h-[180px]',
                'bg-card border-t border-border',
                'shadow-[0_-4px_32px_hsl(var(--foreground)/0.06)]',
                'flex items-center',
                'transition-[transform,opacity] duration-[450ms] ease-[cubic-bezier(0.34,1.30,0.64,1)]',
                visible ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0',
              ),
        )}
      >
        <div
          className={cn(
            'flex items-center',
            variant === 'inline'
              ? animationOnly
                ? 'w-auto flex-col gap-2 p-6'
                : 'w-full max-w-xl rounded-xl border border-border bg-card px-2 py-4 shadow-sm'
              : 'h-full w-full',
          )}
        >
          {variant === 'banner' ? (
            <div
              className="absolute top-0 left-0 right-0 h-[2px] pointer-events-none bg-primary/80"
              aria-hidden
            />
          ) : null}

          {hasPending && (
            <>
              <div
                ref={nuggetZoneRef}
                className={cn(
                  'relative flex w-[140px] flex-shrink-0 items-center justify-center',
                  animationOnly ? 'h-[100px]' : 'h-full',
                )}
              >
                <canvas
                  ref={canvasRef}
                  className="absolute inset-0 pointer-events-none w-full h-full"
                />
                <div
                  ref={glowRef}
                  className="absolute w-20 h-20 rounded-full pointer-events-none"
                  style={{
                    opacity: 0,
                    background:
                      'radial-gradient(circle, hsl(var(--primary)/0.45) 0%, hsl(var(--primary)/0.12) 45%, transparent 70%)',
                  }}
                />
                <div
                  ref={stageRef}
                  className="relative z-[1] flex h-[72px] w-[72px] items-center justify-center"
                >
                  <svg
                    ref={svgRef}
                    width={72}
                    height={72}
                    viewBox="0 0 120 120"
                    fill="none"
                    className="block overflow-visible"
                  >
                    <path ref={mainRef} />
                    <path ref={lightRef} />
                    <path ref={drRef} />
                    <path ref={dlRef} />
                  </svg>
                </div>
              </div>

              {animationOnly ? (
                animationLabel ? (
                  <p className="text-sm font-normal text-foreground">{animationLabel}</p>
                ) : null
              ) : (
                <>
                  <div className="flex-shrink-0 w-px h-[60%] bg-border" />
                  <div className="flex-1 flex flex-col justify-center gap-2 px-6 min-w-0">
                    <span className="text-[10px] font-semibold tracking-wider uppercase text-muted-foreground">
                      {t('accountMapping.loader.processing')}
                    </span>
                    <p
                      ref={titleRef}
                      className="text-sm font-semibold text-foreground tracking-tight transition-opacity duration-300"
                    >
                      {t('accountMapping.loader.labels.stage1')}
                    </p>
                    {!hideTabLabels && pendingLabels ? (
                      <p className="text-xs text-muted-foreground">{pendingLabels}</p>
                    ) : null}
                    <p className="text-xs text-muted-foreground">
                      {t('accountMapping.loader.subtitle')}
                    </p>
                    <div className="w-full max-w-xs h-[3px] rounded-full overflow-hidden bg-muted">
                      <div
                        ref={fillRef}
                        className="h-full w-0 rounded-full bg-primary transition-[width] duration-[400ms] ease-[cubic-bezier(0.4,0,0.2,1)]"
                      />
                    </div>
                  </div>
                </>
              )}
            </>
          )}

          {hasCompleted && (
            <>
              {hasPending && <div className="flex-shrink-0 w-px h-[60%] bg-border" />}
              <div className="flex flex-col justify-center gap-1.5 px-6 min-w-0">
                <span className="text-[10px] font-semibold tracking-wider uppercase text-muted-foreground">
                  {t('accountMapping.loader.completed')}
                </span>
                <p className="text-sm text-foreground">{completedLabels.join(', ')}</p>
                {onDismiss && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="mt-1 h-8 w-fit text-muted-foreground hover:text-foreground"
                    onClick={onDismiss}
                  >
                    {t('accountMapping.loader.dismiss')}
                  </Button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}
